/**
 * What the library hands to a day, and which movements are currently chosen to be handed over.
 *
 * ── Why the selection holds PICKS rather than ids ────────────────────────────────────────────
 * A trainer chooses three movements, types a new search, and chooses two more. The first three are
 * no longer in the results, so a selection of ids would have nothing left to resolve them against —
 * the rail only ever holds the narrowed list, and widening it back would mean keeping the whole
 * catalogue in this module's reach. Holding the pick itself makes the selection independent of the
 * query that produced it, which is the behaviour the owner asked for: choose across filters, add
 * once.
 *
 * ── The order is the INSERTION order, and that is why it is an array ─────────────────────────
 * The batch lands in the order the rows were pressed. A `Set` would carry no order and an object
 * keyed by id would carry insertion order only by accident of the engine, so the array is the shape
 * that states the rule.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { ExerciseListItem } from '~/entities/exercise-library';
import type { ExercisePick } from '~/entities/plan-draft';

/** The chosen movements, in the order they were chosen. */
export type LibrarySelection = readonly ExercisePick[];

/**
 * The four fields a day needs from a catalogue row — the ONE place this app maps the library's
 * shape onto the plan's.
 *
 * It was written out three times (the press, the drag payload, the warm-up insert), which is three
 * places to forget `measurementFields` the next time the catalogue grows a field.
 */
export function libraryPick(item: ExerciseListItem): ExercisePick {
  return {
    id: item.id,
    name: item.name,
    typeKey: item.typeKey,
    measurementFields: item.measurementFields,
  };
}

/**
 * Press a row: choose it if it is not chosen, un-choose it if it is.
 *
 * A re-chosen movement goes to the END of the selection rather than back to where it was, because
 * that is the only answer that matches what the trainer just did — the row they pressed last is the
 * one they most recently decided to add.
 */
export function toggleLibrarySelection(
  selection: LibrarySelection,
  pick: ExercisePick,
): LibrarySelection {
  return isLibrarySelected(selection, pick.id)
    ? selection.filter((chosen) => chosen.id !== pick.id)
    : [...selection, pick];
}

/** Identity is the catalogue's id, never the name — two movements may share a name. */
export function isLibrarySelected(selection: LibrarySelection, id: number): boolean {
  return selection.some((chosen) => chosen.id === id);
}
